import { useState } from "react";
import { C, inputStyle } from "./theme.js";

/* ============================================================================
   ציוד ומטלטלין של הנכס — בחירה מרשימה מוכנה + הוספה ידנית.

   התוצאה היא טקסט, שורה לפריט ("מזגן ×2"), בדיוק המבנה של apt.inventory:
   הטופס הידני, העוזר והחוזה (נספח א' — "מטלטלין שנמסרו לשוכר") כבר
   קוראים וכותבים אותו. הרכיב הזה רק חוסך את ההקלדה.
   ============================================================================ */

export const INVENTORY_PRESETS = [
  { group: "מטבח", items: ["מקרר", "תנור אפייה", "כיריים", "קולט אדים", "מיקרוגל", "מדיח כלים", "ארונות מטבח"] },
  { group: "כביסה ומים", items: ["מכונת כביסה", "מייבש כביסה", "דוד שמש", "דוד חשמל"] },
  { group: "חדרים וסלון", items: [["מזגן", true], "מיטה זוגית", ["מיטת יחיד", true], ["מזרן", true], ["ארון בגדים", true], "שידה", "ספה", "שולחן אוכל", ["כיסאות", true], "שולחן סלון", "טלוויזיה", "מדפים", "גופי תאורה", "וילונות", "תריסים חשמליים"] },
  { group: "כללי", items: ["שלט לחניה", "מחסן", "ציוד גינה", "אינטרקום", "מערכת אזעקה"] },
].map((g) => ({
  group: g.group,
  items: g.items.map((it) => (Array.isArray(it) ? { label: it[0], qty: !!it[1] } : { label: it, qty: false })),
}));

const ALL_PRESETS = INVENTORY_PRESETS.flatMap((g) => g.items);

/** שורות הרשימה, בלי ריקות. */
export const inventoryLines = (text) => String(text || "").split("\n").map((x) => x.trim()).filter(Boolean);

/** "5 פריטים: מקרר, תנור אפייה, מזגן ×2…" — לסיכום האשף וללוג. */
export function inventorySummary(text) {
  const lines = inventoryLines(text);
  if (!lines.length) return "";
  const head = lines.slice(0, 4).join(", ");
  return `${lines.length} פריטים: ${head}${lines.length > 4 ? "…" : ""}`;
}

/* "מזגן ×2" / "מזגן x2" / "2 מזגנים" לא — רק התחביר שאנחנו כותבים. */
const parseLine = (line) => {
  const m = /^(.*?)\s*[×x]\s*(\d+)$/.exec(line);
  return m ? { label: m[1].trim(), qty: Number(m[2]) || 1 } : { label: line.trim(), qty: 1 };
};

export default function InventoryPick({ initial = "", onPick }) {
  /* המצב ההתחלתי נבנה מהטקסט הקיים: פריטים מהרשימה המוכנה מסומנים,
     השאר הופכים ל"פריטים נוספים". */
  const [state] = useState(() => {
    const chosen = {};   // label → qty
    const custom = [];
    for (const line of inventoryLines(initial)) {
      const { label, qty } = parseLine(line);
      if (ALL_PRESETS.some((p) => p.label === label)) chosen[label] = qty;
      else custom.push(line);
    }
    return { chosen, custom };
  });
  const [chosen, setChosen] = useState(state.chosen);
  const [custom, setCustom] = useState(state.custom);
  const [draft, setDraft] = useState("");

  /* נבחר = המפתח קיים (גם כשהכמות ריקה באמצע עריכה). */
  const isOn = (label) => chosen[label] !== undefined;
  const toggle = (label) => setChosen((c) => {
    const next = { ...c };
    if (next[label] !== undefined) delete next[label]; else next[label] = 1;
    return next;
  });
  /* בזמן ההקלדה השדה יכול להיות ריק — אחרת אי אפשר למחוק את ה-1 כדי
     להקליד 2 (ההקלדה נדבקת: "12"). ריק הופך ל-1 ביציאה מהשדה. */
  const setQty = (label, raw) => setChosen((c) => ({
    ...c,
    [label]: raw === "" ? "" : Math.max(1, Math.min(99, Math.round(Number(raw)) || 1)),
  }));
  const fixQty = (label) => setChosen((c) => (c[label] === "" ? { ...c, [label]: 1 } : c));
  const addCustom = () => {
    const t = draft.trim();
    if (!t) return;
    // כמה פריטים בבת אחת: "מקרר, תנור, מיקרוגל"
    const parts = t.split(/[,\n]+/).map((x) => x.trim()).filter(Boolean);
    setCustom((l) => [...l, ...parts.filter((p) => !l.includes(p))]);
    setDraft("");
  };

  const lines = [
    ...ALL_PRESETS.filter((p) => isOn(p.label)).map((p) => {
      const q = Number(chosen[p.label]) || 1;
      return q > 1 ? `${p.label} ×${q}` : p.label;
    }),
    ...custom,
  ];

  return (
    <div className="mt-2 rounded-lg border p-3" style={{ borderColor: C.line }}>
      <div className="text-sm font-medium">ציוד ומטלטלין בנכס</div>
      <div className="text-[11px] mb-2" style={{ color: C.muted }}>
        סמנו מהרשימה או הוסיפו ידנית. הרשימה נכנסת לחוזה כנספח א' ("מטלטלין שנמסרו לשוכר") ותמולא מראש לכל דייר.
      </div>

      {INVENTORY_PRESETS.map((g) => (
        <div key={g.group} className="mb-2">
          <div className="text-[11px] mb-1" style={{ color: C.ochre, fontWeight: 600 }}>{g.group}</div>
          <div className="flex flex-wrap gap-1.5">
            {g.items.map((p) => {
              const on = isOn(p.label);
              return (
                <label key={p.label} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] cursor-pointer"
                  style={{ border: `1px solid ${on ? C.teal : C.line}`, background: on ? "#E8F3F1" : "#fff", color: on ? C.teal : C.ink }}>
                  <input type="checkbox" className="sr-only" checked={on} onChange={() => toggle(p.label)} />
                  <span>{on ? "✓ " : ""}{p.label}</span>
                  {on && p.qty && (
                    <input type="number" min="1" max="99" inputMode="numeric" aria-label={`כמות ${p.label}`}
                      value={chosen[p.label]} onChange={(e) => setQty(p.label, e.target.value)}
                      onBlur={() => fixQty(p.label)}
                      /* לחיצה בוחרת את המספר — הקלדה מחליפה אותו במקום להתווסף אחריו. */
                      onFocus={(e) => e.target.select()}
                      style={{ ...inputStyle, width: 50, padding: "1px 4px", fontSize: 12, textAlign: "center" }} />
                  )}
                </label>
              );
            })}
          </div>
        </div>
      ))}

      <div className="mt-2">
        <div className="text-[11px] mb-1" style={{ color: C.ochre, fontWeight: 600 }}>פריטים נוספים</div>
        {custom.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {custom.map((c) => (
              <span key={c} className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px]" style={{ border: `1px solid ${C.teal}`, background: "#E8F3F1", color: C.teal }}>
                ✓ {c}
                <button type="button" onClick={() => setCustom((l) => l.filter((x) => x !== c))} aria-label={`הסרת ${c}`} style={{ color: C.late }}>✕</button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-1.5">
          <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="למשל: מכונת קפה, סט מזרנים, שולחן כתיבה"
            aria-label="פריט נוסף" style={{ ...inputStyle, flex: 1, padding: "6px 9px", fontSize: 13 }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} />
          <button type="button" onClick={addCustom} disabled={!draft.trim()} className="rounded-lg px-3 text-[12px]"
            style={{ border: `1px solid ${C.line}`, color: C.teal, opacity: draft.trim() ? 1 : 0.5 }}>הוספה</button>
        </div>
      </div>

      <div className="text-[11px] mt-2" style={{ color: C.muted }}>
        {lines.length ? `${lines.length} פריטים ברשימה` : "עדיין לא נבחר פריט — אפשר גם לדלג ולהשלים אחר כך בכרטיס הנכס."}
      </div>
      <button type="button" onClick={() => onPick(lines.join("\n"))}
        className="mt-2 w-full rounded-lg px-3 py-2 text-[12px]" style={{ background: C.teal, color: "#fff" }}>
        {lines.length ? "אישור הרשימה" : "המשך בלי ציוד"}
      </button>
    </div>
  );
}
